import { useCallback, useState } from 'react'

import { Button, FixedBanner } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { useModal } from '@circlefin/modal-router'
import { routes } from '@services/sections/modal'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  CardPaymentMethodsDocument,
  useAddPaymentMethodMutation,
  useCreateCardSetupMutation,
  WalletPaymentMethodDocument,
} from '@shared/graphql'
import {
  AddressElement,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@shared/stripe'
import {
  CardNumberElement as StripeCardElement,
  AddressElement as StripeAddressElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'

import type { SubmitHandler } from '@circlefin/form/useForm'

const schema = y.object({
  /**
   * Set as the default credit card?
   */
  isDefault: y.boolean().required(),
})

type AddPaymentMethodSchema = y.InferType<typeof schema>

export interface StripeElementsProps {
  /**
   * Optional invoice ID (if routing back to pay invoice modal after adding card).
   */
  invoiceId?: string
}

export const AddPaymentMethod: React.FC<StripeElementsProps> = ({
  invoiceId,
}) => {
  const { t } = useTranslation('modals/settings/billing')
  const stripe = useStripe()
  const elements = useElements()
  const { router, close } = useModal()
  const { data: session } = useSession()
  const [stripeError, setStripeError] = useState<string>()
  const [Form] = useForm<AddPaymentMethodSchema>({
    schema,
    defaultValues: {
      isDefault: true,
    },
  })
  const [createCardSetupIntent, createCardSetupIntentResult] =
    useCreateCardSetupMutation()
  const [addPaymentMethodMutation, addPaymentMethodMutationResult] =
    useAddPaymentMethodMutation({
      refetchQueries: [CardPaymentMethodsDocument, WalletPaymentMethodDocument],
      awaitRefetchQueries: true,
    })

  const handleSubmit: SubmitHandler<AddPaymentMethodSchema> = useCallback(
    async (values) => {
      setStripeError('')

      if (!session) {
        setStripeError(t`common:generalError`)
        return
      }

      const { data: createCardSetupIntentData } = await createCardSetupIntent({
        variables: { userEmail: session.user.email },
        onError: () => null,
      })

      if (!createCardSetupIntentData || !stripe || !elements) {
        setStripeError(t`common:generalError`)
        return
      }

      const addressElement = elements.getElement(StripeAddressElement)
      const cardElement = elements.getElement(StripeCardElement)

      if (!addressElement || !cardElement) {
        setStripeError(t`common:generalError`)
        return
      }

      // getValue triggers validations
      const address = await addressElement.getValue()
      if (!address.complete) {
        return
      }

      const { error: setupIntentError, setupIntent } =
        await stripe.confirmCardSetup(
          createCardSetupIntentData.createCardSetup.clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: address.value,
            },
          },
        )

      if (setupIntentError) {
        setStripeError(setupIntentError.message)
        return
      }

      if (setupIntent.payment_method == null) {
        setStripeError(t`common:generalError`)
        return
      }

      const paymentMethodId =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id

      await addPaymentMethodMutation({
        variables: {
          isDefault: values.isDefault,
          paymentProviderPaymentMethodId: paymentMethodId,
        },
        onError: () => null,
        onCompleted: () => {
          if (invoiceId) {
            router.push({
              pathname:
                routes.account.billing.payInvoice.paymentMethodSelection,
              query: { invoiceId },
            })
          } else {
            close()
          }
        },
      })
    },
    [
      session,
      createCardSetupIntent,
      stripe,
      elements,
      addPaymentMethodMutation,
      t,
      invoiceId,
      router,
      close,
    ],
  )

  const handleCancel = useCallback(() => {
    close()
  }, [close])

  return (
    <GraphQLErrorBoundary
      error={createCardSetupIntentResult.error}
      retry={createCardSetupIntentResult.reset}
    >
      <GraphQLErrorBoundary
        error={addPaymentMethodMutationResult.error}
        retry={addPaymentMethodMutationResult.reset}
      >
        <Form
          className="flex w-full max-w-112 flex-col space-y-6 self-center"
          data-testid="add-card-payment-method-stripe"
          onSubmit={handleSubmit}
        >
          <div className="w-full space-y-4">
            <div>
              <CardNumberElement
                label={t('addCreditCard.details.cardNumber')}
              />
            </div>
            <span className="flex gap-4">
              <div className="w-full">
                <CardExpiryElement
                  label={t('addCreditCard.details.cardExpiry')}
                />
              </div>
              <div className="w-full">
                <CardCvcElement
                  label={t('addCreditCard.details.securityCode')}
                />
              </div>
            </span>
            <Form.Checkbox
              className="mt-6"
              label={t('addCreditCard.details.setDefault')}
              name="isDefault"
            />
          </div>
          <AddressElement />
          <div className="flex gap-4">
            <Form.SubmitButton className="w-2/3" variant="primary">
              {t('settings:billing.addCard')}
            </Form.SubmitButton>
            <Button
              className="w-1/3"
              onClick={handleCancel}
              variant="secondary"
            >
              {t('common:cancel')}
            </Button>
          </div>

          {stripeError && (
            <FixedBanner status="error">
              <FixedBanner.Description>{stripeError}</FixedBanner.Description>
            </FixedBanner>
          )}
        </Form>
      </GraphQLErrorBoundary>
    </GraphQLErrorBoundary>
  )
}
